import makeStyles from '@material-ui/core/styles/makeStyles'
import { ClassNameMap } from '@material-ui/core/styles/withStyles'
import parse, {
    DOMNode,
    Element,
    HTMLReactParserOptions,
    domToReact
} from 'html-react-parser'
import { useMemo } from 'react'
import { scrollbarStyles } from 'styles/shared.styles'

export const useHTMLStyles = makeStyles((theme) => ({
    html: {
        ...scrollbarStyles,
        marginTop: '2vh',
        overflow: 'overlay'
    },
    body: {
        marginTop: '4vh',
        paddingRight: '4vh',
        paddingLeft: '4vh'
    },
    p: {
        margin: 0
    },
    img: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        margin: '8vh auto',

        '& img': {
            maxWidth: 900,
            height: 'auto',
            borderColor: theme.custom.colors.oxfordBlue,
            borderWidth: 5,
            padding: 5,
            borderStyle: 'double'
        }
    },
    a: {
        color: theme.palette.primary.main,
        textDecoration: 'underline'
    }
}))

const headings = ['h2', 'h3', 'h4', 'h5'] as const

type HeadingCounter = {
    h2: number
    h3: number
    h4: number
    h5: number
}

type DOMNodeCorrected = DOMNode & {
    name?: string
    children?: Array<{ data: string }>
    parent: {
        name: string
    }
}

const isEmailAddress = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(value)
}

const parseHTML = (
    htmlContent: string,
    styles: ClassNameMap<'html' | 'body' | 'p' | 'img' | 'a'>
) => {
    const headingCount: HeadingCounter = {
        h2: 0,
        h3: 0,
        h4: 0,
        h5: 0
    }

    const options: HTMLReactParserOptions = {
        // eslint-disable-next-line sonarjs/cognitive-complexity
        replace(domNode: DOMNode) {
            const node = domNode as DOMNodeCorrected
            if (node instanceof Element && node.attribs) {
                // Remove attributes from HTML tags

                for (const attrName in node.attribs) {
                    if (
                        Object.prototype.hasOwnProperty.call(
                            node.attribs,
                            attrName
                        )
                    ) {
                        if (node.attribs[attrName] && node.name !== 'img') {
                            delete node.attribs[attrName]
                        }

                        if (node.name === 'img' && attrName === 'title') {
                            delete node.attribs[attrName]
                        }
                    }
                }

                // Strip head element
                if (node.name === 'head') {
                    return <></>
                }

                // Replace HTML & BODY
                if (node.name === 'html' || node.name === 'body') {
                    return (
                        <div className={styles[node.name]}>
                            {domToReact(node.children, options)}
                        </div>
                    )
                }

                // Apply number ordering to heading elements, respecting if it's h2 / h3 / ... etc.
                if (headings.includes(node.name as any)) {
                    const heading = node.name as (typeof headings)[number]
                    headingCount[heading] += 1

                    node.children[0].data = `${headingCount[heading]}. ${node.children[0].data}`
                }

                // Handle support@genomate.health links
                if (
                    node.name === 'a' &&
                    node?.children[0]?.data &&
                    isEmailAddress(node.children[0].data)
                ) {
                    return (
                        <a
                            className={styles['a']}
                            href={`mailto:${node.children[0].data}`}
                        >
                            {domToReact(node.children, options)}
                        </a>
                    )
                }

                if (node.name === 'p' && node.parent.name === 'li') {
                    return (
                        <p className={styles[node.name]}>
                            {domToReact(node.children, options)}
                        </p>
                    )
                }
            }
            return node
        },
        transform(reactNode, domNode, index) {
            // Center images in the page with some padding
            if (
                domNode instanceof Element &&
                domNode.attribs &&
                domNode.name === 'img'
            ) {
                return (
                    <span className={styles[domNode.name]} key={index}>
                        {reactNode}
                    </span>
                )
            }

            return reactNode
        }
    }

    return parse(htmlContent, options)
}

export const useParseHTML = (html: string | null) => {
    const styles = useHTMLStyles()
    return useMemo(() => {
        if (!html) return null

        return parseHTML(
            `<html>
                <body>${html}</body>
            </html>`,
            styles
        )
    }, [html, styles])
}
